/**
 * Inline SVG icon set.
 *
 * The Figma uses coolicons. These are redrawn on the same 24px grid rather
 * than exported, so they inherit `currentColor` and cost no extra request —
 * on a paid-traffic landing page an icon sprite is a round trip we do not
 * need to spend.
 *
 * Every icon is decorative: the accessible name always lives on the control
 * that wraps it, so nothing here is exposed to assistive technology.
 */
const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
};

function Svg({ size = 24, children, ...rest }) {
  return (
    <svg {...base} width={size} height={size} {...rest}>
      {children}
    </svg>
  );
}

export const ArrowRight = (p) => (
  <Svg {...p}>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </Svg>
);

export const ArrowLeft = (p) => (
  <Svg {...p}>
    <path d="M19 12H5M11 18l-6-6 6-6" />
  </Svg>
);

export const ChevronDown = (p) => (
  <Svg {...p}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);

export const MapPin = (p) => (
  <Svg {...p}>
    <path d="M20 10c0 4.4-5.4 9.6-7.3 11.3a1 1 0 0 1-1.4 0C9.4 19.6 4 14.4 4 10a8 8 0 1 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </Svg>
);

export const ListIcon = (p) => (
  <Svg {...p}>
    <path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
  </Svg>
);

export const Lock = (p) => (
  <Svg {...p} strokeWidth={2}>
    <rect x="4" y="10" width="16" height="11" rx="2.5" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </Svg>
);

export const HomeIcon = (p) => (
  <Svg {...p} fill="currentColor" stroke="none">
    <path d="M11.3 2.7a1 1 0 0 1 1.4 0l8 7.4a1 1 0 0 1 .3.8V20a2 2 0 0 1-2 2h-4v-6h-4v6H5a2 2 0 0 1-2-2v-9.1a1 1 0 0 1 .3-.8Z" />
  </Svg>
);

export const BriefcaseIcon = (p) => (
  <Svg {...p} fill="currentColor" stroke="none">
    <path d="M9 3a2 2 0 0 0-2 2v1H4a2 2 0 0 0-2 2v3h20V8a2 2 0 0 0-2-2h-3V5a2 2 0 0 0-2-2Zm0 3V5h6v1Z" />
    <path d="M2 13v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6h-8v1.5a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1V13Z" />
  </Svg>
);

export const Wifi = (p) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M2.5 8.5a15.5 15.5 0 0 1 19 0M5.5 12.4a11 11 0 0 1 13 0M8.6 16.3a6.4 6.4 0 0 1 6.8 0" />
    <circle cx="12" cy="20" r="1.2" fill="currentColor" stroke="none" />
  </Svg>
);

export const WifiOff = (p) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M2.5 8.5a15.5 15.5 0 0 1 6-3.6M15.5 5a15.4 15.4 0 0 1 6 3.5M5.5 12.4a11 11 0 0 1 3-1.9M8.6 16.3a6.4 6.4 0 0 1 3-1.2" />
    <circle cx="12" cy="20" r="1.2" fill="currentColor" stroke="none" />
    <path d="M3 3l18 18" />
  </Svg>
);

export const CheckCircle = (p) => (
  <Svg {...p} strokeWidth={2}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.5 2.5 2.5 4.5-5" />
  </Svg>
);

export const XCircle = (p) => (
  <Svg {...p} strokeWidth={2}>
    <circle cx="12" cy="12" r="9" />
    <path d="m9 9 6 6M15 9l-6 6" />
  </Svg>
);

/* The 404 mark — boxicons `bx:error`, redrawn on the same 24px grid. */
export const AlertTriangle = (p) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M10.3 4 2.7 17.3a2 2 0 0 0 1.7 3h15.2a2 2 0 0 0 1.7-3L13.7 4a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9.5v4" />
    <path d="M12 16.8h.01" />
  </Svg>
);

export const Tv = (p) => (
  <Svg {...p} strokeWidth={2}>
    <rect x="2.5" y="7" width="19" height="13" rx="2.5" />
    <path d="m8 3 4 4 4-4" />
  </Svg>
);

export const Laptop = (p) => (
  <Svg {...p} strokeWidth={2}>
    <rect x="4" y="5" width="16" height="11" rx="2" />
    <path d="M2 20h20" />
  </Svg>
);

export const Camera = (p) => (
  <Svg {...p} strokeWidth={2}>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17" cy="7" r="1.1" fill="currentColor" stroke="none" />
  </Svg>
);

export const PlayCircle = (p) => (
  <Svg {...p} strokeWidth={2}>
    <circle cx="12" cy="12" r="9" />
    <path d="M10.5 9.2v5.6l4.5-2.8Z" fill="currentColor" />
  </Svg>
);

export const Sparkles = (p) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M12 3.5 13.7 9l5.5 1.7-5.5 1.7L12 18l-1.7-5.6L4.8 10.7 10.3 9Z" />
    <path d="M18.5 4v3M20 5.5h-3" />
  </Svg>
);

export const User = (p) => (
  <Svg {...p} strokeWidth={2}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
  </Svg>
);

export const Users = (p) => (
  <Svg {...p} strokeWidth={2}>
    <circle cx="9" cy="7" r="4" />
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
  </Svg>
);

export const UsersThree = (p) => (
  <Svg {...p} strokeWidth={2}>
    {/* A centre figure flanked by two partials — reads as a crowd at 56px
        without the outer two competing with the middle one. */}
    <circle cx="12" cy="8" r="3.4" />
    <path d="M6 20a6 6 0 0 1 12 0" />
    <path d="M18 6.4a2.9 2.9 0 0 1 0 5.6" />
    <path d="M22.5 18.4a4.4 4.4 0 0 0-3-3.7" />
    <path d="M6 6.4a2.9 2.9 0 0 0 0 5.6" />
    <path d="M1.5 18.4a4.4 4.4 0 0 1 3-3.7" />
  </Svg>
);

export const Gauge = (p) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M4 17a9 9 0 1 1 16 0" />
    <path d="m14.5 10.5-3 4" />
  </Svg>
);

export const ShieldCheck = (p) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M12 2.8 20 6v6.2c0 4.6-3.4 7.6-8 9.2-4.6-1.6-8-4.6-8-9.2V6Z" />
    <path d="m9 12 2 2 4-4.5" />
  </Svg>
);

export const Tag = (p) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M3.5 11.2V4.8a1.3 1.3 0 0 1 1.3-1.3h6.4a1.3 1.3 0 0 1 .9.4l8 8a1.3 1.3 0 0 1 0 1.8l-6.3 6.3a1.3 1.3 0 0 1-1.8 0l-8-8a1.3 1.3 0 0 1-.5-.8Z" />
    <circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none" />
  </Svg>
);

export const Phone = (p) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M6.6 3.5h3l1.5 3.8-1.9 1.4a11.5 11.5 0 0 0 5.1 5.1l1.4-1.9 3.8 1.5v3a2 2 0 0 1-2.2 2A16.5 16.5 0 0 1 4.6 5.7a2 2 0 0 1 2-2.2Z" />
  </Svg>
);

export const Dots = (p) => (
  <Svg {...p}>
    <circle cx="5" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none" />
    <circle cx="19" cy="12" r="1.6" fill="currentColor" stroke="none" />
  </Svg>
);

export const Check = (p) => (
  <Svg {...p} strokeWidth={3}>
    <path d="M20 6 9 17l-5-5" />
  </Svg>
);

export const PoundSterling = (p) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M14.5 7.5A3.2 3.2 0 0 0 8.4 9v4.2c0 2-1 3.4-2.4 4.3h11" />
    <path d="M7 12.6h6" />
  </Svg>
);

export const Zap = (p) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M13.5 2.5 4.8 13.2a.7.7 0 0 0 .5 1.1h5.1l-.9 7.2 8.7-10.7a.7.7 0 0 0-.5-1.1h-5.1Z" />
  </Svg>
);

export const Handshake = (p) => (
  <Svg {...p}>
    <path d="m11 17 2 2a1 1 0 1 0 3-3" />
    <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
    <path d="m21 3 1 11h-2" />
    <path d="M3 3l5 5" />
    <path d="M3 4h8" />
    <path d="m11 4-6.06 6.06a2 2 0 0 0 0 2.83L9 17" />
  </Svg>
);

export const Search = (p) => (
  <Svg {...p} strokeWidth={2}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Svg>
);

/**
 * The oversized signal-fan watermark the Figma floats in the corner of the
 * short page heroes and the journey sections. Its own viewBox, since it is a
 * background flourish rather than a 24px icon.
 */
export const WifiArcs = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 200 140"
    fill="none"
    stroke="currentColor"
    strokeWidth="6"
    strokeLinecap="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M4.5 47.5a135 135 0 0 1 191 0" />
    <path d="M33 76a95 95 0 0 1 134 0" />
    <path d="M62 104.5a55 55 0 0 1 76 0" />
    <circle cx="100" cy="130" r="7" fill="currentColor" stroke="none" />
  </svg>
);

/* --- social ------------------------------------------------------------- */

export const Facebook = (p) => (
  <Svg {...p} fill="currentColor" stroke="none">
    <path d="M13.5 21v-7.5h2.6l.4-3h-3V8.6c0-.9.3-1.5 1.5-1.5H16.6V4.4A20 20 0 0 0 14.3 4.3c-2.3 0-3.9 1.4-3.9 4v2.2H7.8v3h2.6V21Z" />
  </Svg>
);

export const Twitter = (p) => (
  <Svg {...p} fill="currentColor" stroke="none">
    <path d="M21 6.2a7 7 0 0 1-2 .6 3.5 3.5 0 0 0 1.5-1.9 7 7 0 0 1-2.2.9 3.5 3.5 0 0 0-6 3.2A9.9 9.9 0 0 1 4.1 5.3a3.5 3.5 0 0 0 1.1 4.7 3.4 3.4 0 0 1-1.6-.4 3.5 3.5 0 0 0 2.8 3.5 3.5 3.5 0 0 1-1.6.1 3.5 3.5 0 0 0 3.3 2.4A7 7 0 0 1 3 17.1a9.9 9.9 0 0 0 5.4 1.6c6.4 0 10-5.4 9.8-10.2A7 7 0 0 0 21 6.2Z" />
  </Svg>
);

export const Instagram = (p) => (
  <Svg {...p} strokeWidth={2}>
    <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
    <circle cx="12" cy="12" r="3.6" />
    <circle cx="16.9" cy="7.1" r="1" fill="currentColor" stroke="none" />
  </Svg>
);

export const Linkedin = (p) => (
  <Svg {...p} fill="currentColor" stroke="none">
    <path d="M6.9 8.9H4.2V20h2.7ZM5.5 4a1.6 1.6 0 1 0 0 3.2 1.6 1.6 0 0 0 0-3.2ZM20 13.3c0-2.9-1.6-4.3-3.7-4.3a3.2 3.2 0 0 0-2.9 1.6h-.1V8.9H10.7V20h2.7v-5.5c0-1.5.3-2.9 2.1-2.9s1.8 1.7 1.8 3V20H20Z" />
  </Svg>
);

/* Provider tiles on the "current provider" step use exported artwork rather
   than an icon, so they are not part of this set. */
